import {
	ApartmentOutlined,
	BarChartOutlined,
	CloseOutlined,
	DeleteOutlined,
	EditOutlined,
	ExclamationCircleFilled,
	EyeOutlined,
	FileAddOutlined,
	FullscreenExitOutlined,
	FullscreenOutlined,
	FormOutlined,
	IdcardOutlined,
	MergeCellsOutlined,
	UserAddOutlined,
} from '@ant-design/icons'
import { Button, Card, Col, Input, Modal, Row, Select, Space } from 'antd'
import dayjs from 'dayjs'
import React, { useEffect, useState, useCallback, useRef } from 'react'
import { departmentService, shiftService } from '../../common/service'
import '../../common/styles/global.scss'
import { useNavigate } from 'react-router-dom'
import GlobalHeader from '../../common/components/Header/GlobalHeader'
import PageHeader from '../../common/components/Header/PageHeader'
import { useTheme, useThemeColors } from '../../common/ThemeContext'
import { DATE_FORMATS } from '../../common/constant'
import { toGenderLabel } from '../../common/service/CommonService'
import { staffService } from '../../common/service/staff.service'
import { AntTable } from '../../common/components/Table/AntTable'
import ViewStaffDetail from './StaffDetail'
import StaffCreateUpdate from './StaffCreateUpdate'
import StaffDashboard from './StaffDashboard'
import StaffMassEdit from './StaffMassEdit'
import StaffOrgChart from './StaffOrgChart'

interface Props {}
const PAGE_SIZE = 10

const normalizePhoneForSearch = (value?: string) => {
	const raw = String(value || '').replace(/-/g, '').replace(/\s/g, '')
	if (!raw) return ''

	let national = raw
	if (national.startsWith('+855')) national = national.slice(4)
	else if (national.startsWith('855')) national = national.slice(3)
	else if (national.startsWith('0')) national = national.slice(1)

	national = national.replace(/\D/g, '')
	if (!national) return ''
	return `0${national}`
}

const StaffList = (props: Props) => {
	const theme = useThemeColors()
	const { isDarkMode } = useTheme()
	const { primary } = theme
	const navigate = useNavigate()
	const [data, setData] = useState<Array<any>>([])
	const [filteredData, setFilteredData] = useState<Array<any>>([])
	const [loading, setLoading] = useState<boolean>(false)
	const [departmentOptions, setDepartmentOptions] = useState<
		Array<{ label: string; value: string }>
	>([])
	const [shiftOptions, setShiftOptions] = useState<Array<{ label: string; value: string }>>([])
	const [viewStaffId, setViewStaffId] = useState<string | null>(null)
	const [editStaffId, setEditStaffId] = useState<string | null>(null)
	const [showOrgChart, setShowOrgChart] = useState<boolean>(false)
	const [orgChartFullscreen, setOrgChartFullscreen] = useState<boolean>(false)
	const [showDashboard, setShowDashboard] = useState<boolean>(false)
	const [showMassEdit, setShowMassEdit] = useState<boolean>(false)
	const [cursorPagination, setCursorPagination] = useState({
		current: 1,
		pageSize: PAGE_SIZE,
		total: 0,
		firstDocId: undefined as string | undefined,
		lastDocId: undefined as string | undefined,
		hasNextPage: false,
		hasPrevPage: false,
	})
	const cursorPaginationRef = useRef(cursorPagination)
	cursorPaginationRef.current = cursorPagination
	const [tableState, setTableState] = useState<any>({
		search: '',
		phoneSearch: '',
		nameSearch: '',
		department: undefined,
		shift: undefined,
		startDate: dayjs().format(DATE_FORMATS.DISPLAY),
		endDate: dayjs().format(DATE_FORMATS.DISPLAY),
	})

	const onDelete = (id: string) => {
		const deleteStaff = async () => {
			return await staffService
				.delete(id)
				.then((res) => {
					Modal.success({
						title: `Staff ${id} Deleted Successfully!`,
						content: `Server Response: ${res.data.message}`,
						onOk: () => window.location.reload(),
					})
				})
				.catch((error) => {
					Modal.error({
						title: 'Staff Delete Failed!',
						content: `Server Response: ${error.response.data.message}`,
					})
				})
		}
		Modal.confirm({
			title: `Are you sure to delete this record?`,
			icon: <ExclamationCircleFilled />,
			content: `Staff ID: ${id}`,
			onOk() {
				deleteStaff()
			},
		})
	}

	const closeViewModal = () => {
		setViewStaffId(null)
	}

	const closeEditModal = () => {
		setEditStaffId(null)
	}

	const toRankingLabel = (value?: string) => {
		if (value === 'manager_level_1') return 'Manager Level 1'
		if (value === 'manager_level_2') return 'Manager Level 2'
		if (value === 'manager_level_3') return 'Manager Level 3'
		if (value === 'executor') return 'Executor'
		return value || '-'
	}

	const tableHeader = [
		{
			title: 'Actions',
			render: (item: { id: string }) => (
				<Space size={'small'}>
					<Button
						size='small'
						type='link'
						onClick={() => setViewStaffId(item?.id)}
						icon={<EyeOutlined />}
					/>
					<Button
						size='small'
						type='link'
						onClick={() => setEditStaffId(item?.id)}
						icon={<EditOutlined />}
					/>
					<Button
						danger
						type='link'
						size='small'
						onClick={() => onDelete(item.id)}
						icon={<DeleteOutlined />}
					/>
				</Space>
			),
		},
		{ title: 'Staff ID', dataIndex: 'id', key: 'id' },
		{
			title: 'Name in Khmer',
			render: (item: { lastName: string; firstName: string }) =>
				`${item?.lastName} ${item?.firstName}`,
		},
		{ title: 'Name in English', dataIndex: 'nameInEnglish', key: 'nameInEnglish' },
		{
			title: 'Gender',
			dataIndex: 'sex',
			key: 'sex',
			render: (item: string) => toGenderLabel(item),
		},
		{ title: 'Department', dataIndex: 'departmentId', key: 'departmentId' },
		{
			title: 'Team',
			dataIndex: 'shift',
			key: 'shift',
			render: (item: { name: string }) => item?.name,
		},
		{ title: 'Night Team', dataIndex: 'extraShift', key: 'extraShift' },
		{
			title: 'Ranking',
			dataIndex: 'ranking',
			key: 'ranking',
			render: (item: string) => toRankingLabel(item),
		},
		{
			title: '1st Line Manager',
			dataIndex: 'firstLineManager',
			key: 'firstLineManager',
			render: (item: { id?: string; name?: string }) => item?.name || item?.id || '-',
		},
		{
			title: '2nd Line Manager',
			dataIndex: 'secondLineManager',
			key: 'secondLineManager',
			render: (item: { id?: string; name?: string }) => item?.name || item?.id || '-',
		},
		{
			title: '3rd Line Manager',
			dataIndex: 'thirdLineManager',
			key: 'thirdLineManager',
			render: (item: { id?: string; name?: string }) => item?.name || item?.id || '-',
		},
		{
			title: 'Role',
			dataIndex: 'role',
			key: 'role',
			render: (item: { name: string }) => item?.name,
		},
		{ title: 'Phone Number', dataIndex: 'phoneNumber', key: 'phoneNumber' },
		{ title: 'Email', dataIndex: 'email', key: 'email' },
		{ title: 'Bank Account', dataIndex: 'bankAccountNumber', key: 'bankAccountNumber' },
		{ title: 'Staff Status', dataIndex: 'status', key: 'status' },
	]

	const handleSelectDep = (e: any) => {
		setTableState((prev: any) => ({ ...prev, department: e }))
	}

	const handleClearDep = () => {
		setTableState((prev: any) => ({ ...prev, department: undefined }))
	}

	const handleSelectShift = (e: any) => {
		setTableState((prev: any) => ({ ...prev, shift: e }))
	}

	const handleClearShift = () => {
		setTableState((prev: any) => ({ ...prev, shift: undefined }))
	}

	const onSearch = (value: string) => {
		setTableState((prev: any) => ({ ...prev, search: value }))
	}

	const onNameSearch = (value: string) => {
		setTableState((prev: any) => ({ ...prev, nameSearch: value }))
	}

	const onPhoneSearch = (value: string) => {
		setTableState((prev: any) => ({ ...prev, phoneSearch: value }))
	}

	const onFetchDepartment = useCallback(async () => {
		try {
			const res: any = await departmentService.fetchListing()
			if (res?.statusCode === 200) {
				setDepartmentOptions(
					(res?.data || []).map((e: any) => ({ label: e?.name, value: e?.id })),
				)
			}
		} catch (error) {
			console.log('error', error)
		}
	}, [])

	const onFetchShift = useCallback(async () => {
		try {
			const res: any = await shiftService.fetchListing()
			if (res?.statusCode === 200) {
				setShiftOptions(
					(res?.data || []).map((e: any) => ({ label: e?.name, value: e?.id })),
				)
			}
		} catch (error) {
			console.log('error', error)
		}
	}, [])

	const onFetchStaff = useCallback(
		async (direction: 'reset' | 'next' | 'prev' = 'reset', overridePageSize?: number) => {
			const cursor = cursorPaginationRef.current
			const pageSize = overridePageSize || cursor.pageSize || PAGE_SIZE
			try {
				setLoading(true)
				const nextCurrent =
					direction === 'next'
						? cursor.current + 1
						: direction === 'prev'
							? Math.max(1, cursor.current - 1)
							: 1
				const payload = {
					departmentId: tableState.department || undefined,
					id: tableState?.search || undefined,
					phoneNumber: tableState?.phoneSearch || undefined,
					limit: pageSize,
					lastId: direction === 'next' ? cursor.lastDocId : undefined,
					firstId: direction === 'prev' ? cursor.firstDocId : undefined,
				}

				const res: any = await staffService.fetchListing(payload)
				if (res?.statusCode === 200) {
					const rows = Array.isArray(res?.data) ? res.data : []
					setData(rows)
					const hasNextPage = Boolean(res?.hasNextPage)
					const hasPrevPage = Boolean(res?.hasPrevPage)
					setCursorPagination((prev) => ({
						...prev,
						current: nextCurrent,
						pageSize,
						firstDocId: res?.firstDocId,
						lastDocId: res?.lastDocId,
						hasNextPage,
						hasPrevPage,
						total: (nextCurrent - 1) * pageSize + rows.length + (hasNextPage ? 1 : 0),
					}))
				}
				setLoading(false)
			} catch (error) {
				console.log('error', error)
				setLoading(false)
			}
		},
		[tableState.department, tableState.search, tableState.phoneSearch],
	)

	const onFetchAllStaff = useCallback(async () => {
		try {
			setLoading(true)
			const payload = {
				departmentId: tableState.department || undefined,
				id: tableState.search || undefined,
				limit: 1000,
			}
			const res: any = await staffService.fetchListing(payload)
			if (res?.statusCode === 200) {
				setData(Array.isArray(res?.data) ? res.data : [])
			}
			setLoading(false)
		} catch (error) {
			console.log('error', error)
			setLoading(false)
		}
	}, [tableState.department, tableState.search])

	const handlePaginationChange = (pagination: any) => {
		const cursor = cursorPaginationRef.current
		const nextPage = Number(pagination?.current || 1)
		const nextPageSize = Number(pagination?.pageSize || cursor.pageSize || PAGE_SIZE)

		if (nextPageSize !== cursor.pageSize) {
			setCursorPagination((prev) => ({
				...prev,
				current: 1,
				pageSize: nextPageSize,
				firstDocId: undefined,
				lastDocId: undefined,
				hasNextPage: false,
				hasPrevPage: false,
				total: 0,
			}))
			onFetchStaff('reset', nextPageSize)
			return
		}

		if (nextPage === cursor.current) return

		if (nextPage > cursor.current) {
			if (cursor.hasNextPage) {
				onFetchStaff('next')
			}
			return
		}

		if (nextPage < cursor.current && cursor.hasPrevPage) {
			onFetchStaff('prev')
		}
	}

	// Client-side filtering for name and shift search
	useEffect(() => {
		let filtered = data

		// Filter by name
		if (tableState.nameSearch) {
			const searchLower = tableState.nameSearch.toLowerCase()
			filtered = filtered.filter((staff) => {
				const khmerName = `${staff?.lastName || ''} ${staff?.firstName || ''}`.toLowerCase()
				const englishName = (staff?.nameInEnglish || '').toLowerCase()
				return khmerName.includes(searchLower) || englishName.includes(searchLower)
			})
		}

		// Filter by shift/team
		if (tableState.shift) {
			filtered = filtered.filter((staff) => staff?.shift?.id === tableState.shift)
		}

		// Filter by phone number (partial match)
		if (tableState.phoneSearch) {
			const searchPhone = normalizePhoneForSearch(tableState.phoneSearch)
			filtered = filtered.filter((staff) =>
				normalizePhoneForSearch(staff?.phoneNumber).includes(searchPhone),
			)
		}

		setFilteredData(filtered)
	}, [data, tableState.nameSearch, tableState.shift, tableState.phoneSearch])

	useEffect(() => {
		onFetchDepartment()
		onFetchShift()
	}, [])

	useEffect(() => {
		if (tableState.nameSearch || tableState.shift || tableState.phoneSearch) {
			onFetchAllStaff()
		} else {
			setCursorPagination((prev) => ({
				...prev,
				current: 1,
				firstDocId: undefined,
				lastDocId: undefined,
				hasNextPage: false,
				hasPrevPage: false,
				total: 0,
			}))
			onFetchStaff('reset')
		}
	}, [tableState.nameSearch, tableState.shift, tableState.phoneSearch, onFetchStaff, onFetchAllStaff])

	return (
		<>
			<GlobalHeader />

			<PageHeader
				title='Staffs'
				subtitle={`${filteredData.length} record${filteredData.length !== 1 ? 's' : ''}`}
				icon={<IdcardOutlined />}
				extra={
					<>
						<Button icon={<BarChartOutlined />} onClick={() => setShowDashboard(true)}>
							Dashboard
						</Button>
						<Button icon={<ApartmentOutlined />} onClick={() => setShowOrgChart(true)}>
							Org Chart
						</Button>
						<Button
							icon={<FileAddOutlined />}
							onClick={() => navigate('/staff_bulk_upload')}
						>
							Bulk Upload
						</Button>
						<Button
							icon={<MergeCellsOutlined />}
							onClick={() => navigate('/staff/merge')}
						>
							Merge staff
						</Button>
						<Button icon={<FormOutlined />} onClick={() => setShowMassEdit(true)}>
							Mass Edit
						</Button>
						<Button
							type='primary'
							ghost={isDarkMode}
							icon={<UserAddOutlined />}
							onClick={() => navigate('/staff/create')}
							style={isDarkMode ? {
								backgroundColor: 'rgba(56, 189, 248, 0.08)',
								fontWeight: 600,
							} : {}}
						>
							New Staff
						</Button>
					</>
				}
			/>

			{/* ── Filter Bar ──────────────────────────────────────────────── */}
			<div style={{ padding: '12px 24px 0' }}>
				<Card
					size='small'
					style={{
						borderRadius: 8,
						boxShadow: theme.cardShadow,
						backgroundColor: isDarkMode ? theme.subBg : '#fff',
						border: `1px solid ${theme.border}`
					}}
					styles={{ body: { padding: '12px 16px' } }}
				>
					<Row gutter={[8, 8]} align='middle' wrap={false}>
						<Col flex='180px'>
							<Input.Search
								placeholder='Search by Staff ID'
								value={tableState.search}
								allowClear
								inputMode='numeric'
								onSearch={onSearch}
								onChange={(e) => onSearch(e.target.value)}
								style={{ width: '100%' }}
							/>
						</Col>
						<Col flex='220px'>
							<Input.Search
								placeholder='Search by Name'
								value={tableState.nameSearch}
								allowClear
								onChange={(e) => onNameSearch(e.target.value)}
								onSearch={onNameSearch}
								style={{ width: '100%' }}
							/>
						</Col>
						<Col flex='220px'>
							<Input.Search
								placeholder='Search by Phone Number'
								value={tableState.phoneSearch}
								allowClear
								onChange={(e) => onPhoneSearch(e.target.value)}
								onSearch={onPhoneSearch}
								style={{ width: '100%' }}
							/>
						</Col>
						<Col flex='200px'>
							<Select
								allowClear
								showSearch
								optionFilterProp='label'
								style={{ width: '100%' }}
								placeholder='Search by Department'
								onSelect={handleSelectDep}
								onClear={handleClearDep}
								value={tableState?.department}
								options={departmentOptions}
							/>
						</Col>
						<Col flex='200px'>
							<Select
								allowClear
								showSearch
								optionFilterProp='label'
								style={{ width: '100%' }}
								placeholder='Search by Team'
								onSelect={handleSelectShift}
								onClear={handleClearShift}
								value={tableState?.shift}
								options={shiftOptions}
							/>
						</Col>
					</Row>
				</Card>
			</div>

			{/* ── Table ───────────────────────────────────────────────────── */}
			<div style={{ padding: '8px 24px 24px' }}>
				<Card
					style={{ borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
					styles={{ body: { padding: 0 } }}
				>
					<AntTable
						columns={tableHeader}
						dataSource={filteredData}
						rowKey={(record) => record?.id}
						loading={loading}
						serverPagination={!(tableState.nameSearch || tableState.shift || tableState.phoneSearch)}
						pageSizeOptions={['10', '20', '50', '100']}
						paginationState={{
							current: cursorPagination.current,
							pageSize: cursorPagination.pageSize,
							total: cursorPagination.total,
						}}
						onPaginationChange={handlePaginationChange}
					/>
				</Card>
			</div>
			<Modal
				title={null}
				open={showDashboard}
				onCancel={() => setShowDashboard(false)}
				footer={null}
				destroyOnHidden
				width={1100}
			>
				<StaffDashboard />
			</Modal>
			<Modal
				title={
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							gap: 12,
							minHeight: 24,
						}}
					>
						<Space size={8} align='center'>
							<ApartmentOutlined style={{ color: theme.text, fontSize: 14 }} />
							<span
								style={{
									color: theme.text,
									fontWeight: 600,
									fontSize: 18,
									lineHeight: 1.2,
									display: 'inline-block',
								}}
							>
								Staff Org Chart
							</span>
						</Space>
						<Space>
							<Button
								size='small'
								icon={orgChartFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
								onClick={() => setOrgChartFullscreen((v) => !v)}
							>
								{orgChartFullscreen ? 'Minimize' : 'Fullscreen'}
							</Button>
							<Button
								size='small'
								type='text'
								icon={<CloseOutlined />}
								onClick={() => {
									setShowOrgChart(false)
									setOrgChartFullscreen(false)
								}}
							/>
						</Space>
					</div>
				}
				open={showOrgChart}
				onCancel={() => {
					setShowOrgChart(false)
					setOrgChartFullscreen(false)
				}}
				closable={false}
				footer={null}
				destroyOnHidden
				width={orgChartFullscreen ? '100vw' : 1200}
				style={orgChartFullscreen ? { top: 0, paddingBottom: 0, maxWidth: '100vw' } : undefined}
				styles={
					orgChartFullscreen
						? {
								content: { height: '100vh', borderRadius: 0 },
								header: { padding: '12px 16px' },
								body: {
									height: 'calc(100vh - 64px)',
									overflowX: 'auto',
									overflowY: 'auto',
								},
							}
						: { header: { padding: '12px 16px' } }
				}
			>
				{showOrgChart && <StaffOrgChart hideInternalHeader />}
			</Modal>
			<Modal
				title={null}
				open={!!viewStaffId}
				onCancel={closeViewModal}
				footer={null}
				destroyOnHidden
				width={1200}
			>
				{viewStaffId && (
					<ViewStaffDetail
						staffId={viewStaffId}
						embedded
						onClose={closeViewModal}
						onEdit={(id) => {
							setViewStaffId(null)
							setEditStaffId(id)
						}}
					/>
				)}
			</Modal>
			<Modal
				title={null}
				open={!!editStaffId}
				onCancel={closeEditModal}
				footer={null}
				destroyOnHidden
				width={1200}
			>
				{editStaffId && (
					<StaffCreateUpdate
						staffIdOverride={editStaffId}
						embedded
						onClose={closeEditModal}
						onSuccess={() => {
							onFetchStaff('reset')
						}}
					/>
				)}
			</Modal>
			<StaffMassEdit
				open={showMassEdit}
				onClose={() => setShowMassEdit(false)}
				onSuccess={() => onFetchStaff('reset')}
				departmentOptions={departmentOptions}
				shiftOptions={shiftOptions}
			/>
		</>
	)
}

export default StaffList
